import { useEffect, useRef, useState } from 'react';
import type { DemoSong } from '../audio/demo-song';
import { saveAutosave } from '../lib/persistence';

const DEFAULT_DELAY_MS = 1000;

export interface AutosaveState {
  /** ISO timestamp of the last successful save, or null. */
  lastSavedAt: string | null;
  /** Set while a save is in flight, or while waiting for the debounce. */
  pending: boolean;
}

/**
 * Debounced auto-save of the current song to IndexedDB.
 * Re-fires whenever `song` changes, after `delayMs` of inactivity.
 */
export function useAutosave(song: DemoSong, delayMs = DEFAULT_DELAY_MS): AutosaveState {
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstRunRef = useRef(true);

  useEffect(() => {
    // Skip the very first effect — we don't auto-save the initial seed.
    if (firstRunRef.current) {
      firstRunRef.current = false;
      return;
    }
    setPending(true);
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      saveAutosave(song)
        .then(() => {
          setLastSavedAt(new Date().toISOString());
          setPending(false);
        })
        .catch(() => {
          setPending(false);
        });
    }, delayMs);
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [song, delayMs]);

  return { lastSavedAt, pending };
}
